import React from 'react';
import { Modal } from 'react-bootstrap';
import { TranslationUtils, ValidationUtils } from '../../utils';
import { ImageRequestAccessDog, ImageSecurityAlert, ImageUnplugged } from '../../assets/images';
import { AdjustmentErrors, ErrorsPrefix } from '../../types/errors.type';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from '../../routes';
import { Button } from '..';
import './AdjustmentNotAvailable.scss';
import { flowId } from '../../logging/ddLogger';
import { AdjustmentPreviewRequest } from 'guest-data-types-ts-lib';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { StoreState } from '../../types/store.type';

interface AdjustmentNotAvailableProps {
    reason: AdjustmentErrors | null;
    adjustment: AdjustmentPreviewRequest | null;
}

export const AdjustmentNotAvailableModal: React.FC<AdjustmentNotAvailableProps> = (props) => {
    const { reason, adjustment } = props;
    const history = useHistory();
    const phoneNumber = ValidationUtils.validateAndGetEnvVar('REACT_APP_TOLL_FREE_PHONE_NUMBER');
    const { trip } = useSelector((store: StoreState) => store);

    const handleOnHide = () => {
        history.push(AppRoutes.MODIFY_TRIP);
    };

    const getPrefix = (): string => {
        let errorPrefix: string = ErrorsPrefix.EXCEPTION;
        if (reason != null) {
            errorPrefix = ErrorsPrefix[reason] || ErrorsPrefix.EXCEPTION;
        }
        return errorPrefix;
    };

    const getContent = (reason: AdjustmentErrors | null, adjustment: AdjustmentPreviewRequest | null) => {
        switch (reason) {
            case AdjustmentErrors.RESERVATION_SOURCE_EXCLUDED:
            case AdjustmentErrors.POLICY_VALIDATION_FAILED:
                return getNotAvailableContent(adjustment);
            case AdjustmentErrors.PENDING_BALANCE:
                return getPendingBalanceContent();
            default:
                return getAdjustmentNotImplementedContent();
        }
    };

    const getPendingBalanceContent = () => {
        let startNote = TranslationUtils.get('modal_adjustment_pending_balance_note_start');
        if (!_.isNil(adjustment) && adjustment.name === 'lateCheckout') {
            startNote = TranslationUtils.get('modal_lco_adjustment_pending_balance_note_start');
        }
        return (
            <React.Fragment>
                <div id="modal-adjustment-pending-balance">
                    <ImageSecurityAlert className="adjustment-not-available-img" />
                </div>

                <div className="adjustment-not-available-content">
                    <div tabIndex={0}>
                        <h4>{TranslationUtils.get('modal_adjustment_pending_balance_title')}</h4>
                    </div>
                    <p tabIndex={0}>{startNote}</p>
                    <p tabIndex={0}>{TranslationUtils.get('modal_adjustment_pending_balance_note_end')}</p>
                    <Button
                        id={'adjustment-pay-pending-balance-modal-button'}
                        variant="primary"
                        block
                        onClick={() => history.push(AppRoutes.PAYMENT)}
                        label={TranslationUtils.get('payment_form_pay_now')}
                    />
                </div>
            </React.Fragment>
        );
    };

    const getNotAvailableContent = (adjustment: AdjustmentPreviewRequest | null) => {
        let modalTitle = TranslationUtils.get('modal_adjustment_not_available_title');
        let startNote = TranslationUtils.get('modal_adjustment_not_available_note_start');
        let endNote = TranslationUtils.get('modal_adjustment_not_available_note_end');
        if (!_.isNil(adjustment) && adjustment.name === 'lateCheckout') {
            modalTitle = TranslationUtils.get('modal_lco_adjustment_not_available_title');
            // modal_lco_adjustment_not_available_note_start is just for fallback if the getTip fail
            startNote = trip.stay?.late_check_out_warning || TranslationUtils.get('modal_lco_adjustment_not_available_note_start');
        }
        return (
            <React.Fragment>
                <div id={'modal-adjustment-not-available'}>
                    <ImageRequestAccessDog className="adjustment-not-available-img" />
                </div>

                <div className="adjustment-not-available-content">
                    <div tabIndex={0}>
                        <h4>{modalTitle}</h4>
                    </div>
                    <p tabIndex={0}>{startNote}</p>
                    <p tabIndex={0}>{endNote}</p>
                    <Button
                        id={'adjustment-not-available-continue-modal-button'}
                        variant="primary"
                        block
                        onClick={() => history.push(AppRoutes.RESERVATION)}
                        label={TranslationUtils.get('modal_adjustment_not_available_back_to_trip')}
                    />
                </div>
            </React.Fragment>
        );
    };

    const getAdjustmentNotImplementedContent = () => {
        return (
            <React.Fragment>
                <div id="modal-adjustment-not-implemented">
                    <ImageUnplugged className="adjustment-not-available-img" />

                    <div className="adjustment-not-available-content">
                        <div tabIndex={0}>
                            <h4>{TranslationUtils.get('cannot_perform_action_title')}</h4>
                        </div>
                        <p tabIndex={0}>{TranslationUtils.get('try_later_or_call')}</p>
                        <span>
                            [{' '}
                            <a tabIndex={0} href={`tel:${phoneNumber}`}>
                                {phoneNumber}
                            </a>{' '}
                            ]
                        </span>

                        <Button
                            id="adjustment-not-implemented-continue-modal-button"
                            className="mt-3"
                            variant="primary"
                            block
                            onClick={() => history.push(AppRoutes.RESERVATION)}
                            label={TranslationUtils.get('modal_adjustment_not_available_continue')}
                        />
                    </div>
                </div>
            </React.Fragment>
        );
    };

    return (
        <Modal show={!!reason} onHide={handleOnHide}>
            <Modal.Header closeButton />

            <div className="adjustment-not-available">{getContent(reason, adjustment)}</div>

            {reason !== AdjustmentErrors.PENDING_BALANCE && (
                <div className="error-flow-id mr-3">
                    <span className="text-muted">{`ref: ${getPrefix()}-${flowId}`}</span>
                </div>
            )}
        </Modal>
    );
};
